import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo2.png";
import { FaArrowLeft, FaEnvelope, FaComment, FaClock, FaChevronDown } from "react-icons/fa";
import { getSiteSettings } from "../services/api";

const FAQS = [
  {
    q: "How do I fund my UCH wallet?",
    a: "Go to your Dashboard, click \"Fund wallet\", enter the amount you wish to deposit, and complete payment via Remita. Your wallet balance updates instantly once payment is confirmed.",
  },
  {
    q: "How long does a refund take?",
    a: "Refund requests are typically reviewed and processed within 3–5 business days. You'll be notified by email once your refund has been completed.",
  },
  {
    q: "Why is my transaction showing as \"Pending\"?",
    a: "A transaction shows as pending while we wait for payment confirmation from your bank or payment provider. Most pending transactions resolve automatically within a few minutes.",
  },
  {
    q: "Can I pay for someone else's hospital bill?",
    a: "Yes. When paying for a service, select \"Paying for someone\" and fill in the patient's details. You'll still be billed as the payer.",
  },
  {
    q: "How do I reset my password?",
    a: "Click \"Forgot password\" on the login page, enter your registered email, and follow the instructions sent to you to set a new password.",
  },
];

export default function Support() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const name =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
    user?.username ||
    "Patient";
  const initials =
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  const [openFaq, setOpenFaq] = useState(null);
  const [contactFormUrl, setContactFormUrl] = useState("");

  useEffect(() => {
    getSiteSettings().then((s) => setContactFormUrl(s.contact_form_url || "")).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 shrink-0">
              <img src={logo} alt="UCH" className="w-7 h-7 object-contain" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm leading-tight">
                UCH Self Service
              </p>
              <p className="text-gray-400 text-[11px]">University College Hospital</p>
            </div>
          </a>
          {user && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0d3d2e] rounded-full flex items-center justify-center text-white font-bold text-xs">
                {initials}
              </div>
              <span className="text-sm font-semibold text-gray-800 hidden sm:inline">
                {name}
              </span>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate(user ? "/dashboard" : "/")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3d2e] border border-green-200 rounded-xl px-4 py-2 hover:bg-green-50 transition mb-6"
        >
          <FaArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>

        <h1 className="text-3xl font-black text-gray-900 mb-1.5">
          Support &amp; Contact Us
        </h1>
        <p className="text-gray-500 mb-8">
          We're here to help. Reach out via any channel below.
        </p>

        {/* Contact cards */}
        <div className="grid sm:grid-cols-2 gap-5 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
              <FaEnvelope className="text-blue-600 w-4 h-4" />
            </div>
            <p className="font-bold text-gray-900 mb-1">Email</p>
            <p className="text-sm text-gray-500 mb-3">We respond within 24 hours</p>
            <a
              href="mailto:support@uchselfservice.net"
              className="text-sm font-bold text-[#0d3d2e] hover:underline"
            >
              support@uchselfservice.net
            </a>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="w-11 h-11 bg-yellow-50 rounded-xl flex items-center justify-center mb-4">
              <FaComment className="text-yellow-600 w-4 h-4" />
            </div>
            <p className="font-bold text-gray-900 mb-1">Live Chat</p>
            <p className="text-sm text-gray-500 mb-3">Chat with us on WhatsApp</p>
            <a
              href="https://wa.me/2348012345678"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-bold text-[#0d3d2e] hover:underline"
            >
              Open WhatsApp
            </a>
          </div>
        </div>

        {/* Support hours banner */}
        <div className="bg-[#0d3d2e] rounded-2xl px-6 py-4 flex items-center justify-between flex-wrap gap-3 mb-8">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Online now
          </span>
          <div className="flex items-center gap-3 text-white">
            <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
              <FaClock className="w-4 h-4" />
            </div>
            <div>
              <p className="font-bold text-sm">Support Hours</p>
              <p className="text-green-200 text-xs">
                Monday – Sunday, 24/7
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        {contactFormUrl && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-7 mb-6">
            <h2 className="font-bold text-gray-900 text-lg mb-1">Send Us a Message</h2>
            <p className="text-sm text-gray-500 mb-4">Fill the form below and we'll get back to you within 24 hours.</p>
            <iframe
              src={contactFormUrl}
              width="100%"
              height="600"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Contact Form"
              className="rounded-xl"
            >
              Loading form...
            </iframe>
          </div>
        )}

        {/* FAQ */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-7">
          <h2 className="font-bold text-gray-900 text-lg mb-1">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Quick answers to the most common queries.
          </p>
          <div className="divide-y divide-gray-100">
            {FAQS.map((f, i) => (
              <div key={f.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-4 text-left"
                >
                  <span className="text-sm font-semibold text-gray-900">{f.q}</span>
                  <FaChevronDown
                    className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <p className="text-sm text-gray-500 pb-4 -mt-1 leading-relaxed">
                    {f.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
